import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Search, Loader2, X, Plus, ArrowUpDown, PenLine, ExternalLink, Save, Trash2, RefreshCw, LayoutGrid } from "lucide-react";
import { cn } from "@/lib/utils";
import { CrosswordCell, CrosswordCellRef } from "./CrosswordCell";
import { LanguageFlags, LANGUAGE_FLAGS } from "./LanguageFlags";
import { LanguageFlagsEditor } from "./LanguageFlagsEditor";
import { useAuth } from "@/hooks/useAuth";
import { AdminDeleteWordButton } from "./AdminDeleteWordButton";
import { CellLinkDropdown, LinkGroup, CellLinkInfo, LINK_COLORS } from "./CellLinkDropdown";
import { useAdvancedSearchTabs, AdvancedSearchTab } from "@/hooks/useAdvancedSearchTabs";
import { AdvancedSearchOverview } from "./AdvancedSearchOverview";
import { useAutoSearch } from "@/hooks/useAutoSearch";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Language filter options for UI
const LANGUAGE_FILTER_OPTIONS = [
  { flag: "🇸🇪", value: LANGUAGE_FLAGS.SWEDISH, name: "Svenska" },
  { flag: "🇬🇧", value: LANGUAGE_FLAGS.ENGLISH, name: "Engelska" },
  { flag: "🇩🇪", value: LANGUAGE_FLAGS.GERMAN, name: "Tyska" },
  { flag: "🇫🇷", value: LANGUAGE_FLAGS.FRENCH, name: "Franska" },
  { flag: "🇪🇸", value: LANGUAGE_FLAGS.SPANISH, name: "Spanska" },
  { flag: "🇮🇹", value: LANGUAGE_FLAGS.ITALIAN, name: "Italienska" },
];
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ITEMS_PER_PAGE = 50;

import {
  ClueWithLanguage,
  CombinedResult,
  SortOption,
  MAX_LENGTH,
  calculatePossibleLetters,
} from "@/lib/advancedSearchTypes";

// Use AdvancedSearchTab from hook, alias as SearchTab for backward compatibility
type SearchTab = AdvancedSearchTab;
const blockedWords = ['neger', 'negrer', 'negern', 'negrerna'];

function containsBlockedWord(text: string): boolean {
  const lowerText = text.toLowerCase();
  return blockedWords.some(word => lowerText.includes(word));
}

function highlightMatches(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm) return text;
  
  const searchWords = searchTerm.toUpperCase().split(/\s+/).filter(w => w.length > 0);
  if (searchWords.length === 0) return text;
  
  const pattern = new RegExp(`(${searchWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);
  
  return parts.map((part, index) => {
    const isMatch = searchWords.some(word => part.toUpperCase() === word.toUpperCase());
    if (isMatch) {
      return (
        <strong key={index} className="font-bold text-foreground">
          {part}
        </strong>
      );
    }
    return part;
  });
}

function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getTabDisplayName(clueQuery: string): string {
  const trimmed = clueQuery.trim();
  if (!trimmed) return "Ny";
  return trimmed.substring(0, 20);
}

// calculatePossibleLetters is imported from advancedSearchTypes

function filterByLanguage(results: CombinedResult[], languageFilter: number): CombinedResult[] {
  if (languageFilter === 0) return results; // No filter, show all
  return results.filter(r => (r.language & languageFilter) !== 0);
}

function calculateMatchPercentage(clue: string, searchTerm: string, wordLength: number, targetLength: number | null): number {
  const clueUpper = clue.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
  const searchUpper = searchTerm.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
  
  if (!clueUpper || !searchUpper) return 0;
  
  let basePercentage = 0;
  
  if (clueUpper === searchUpper) {
    basePercentage = 100;
  } else {
    const searchWords = searchTerm.toUpperCase().split(/\s+/).filter(w => w.length > 0);
    let matchedChars = 0;
    
    for (const searchWord of searchWords) {
      const cleanSearchWord = searchWord.replace(/[^A-ZÅÄÖ]/g, '');
      if (cleanSearchWord && clueUpper.includes(cleanSearchWord)) {
        matchedChars += cleanSearchWord.length;
      }
    }
    
    basePercentage = Math.round((matchedChars / clueUpper.length) * 100);
    basePercentage = Math.min(basePercentage, 100);
  }
  
  if (targetLength !== null && wordLength !== targetLength) {
    basePercentage = Math.round(basePercentage * 0.7);
  }
  
  return basePercentage;
}

function sortResults(results: CombinedResult[], option: SortOption): CombinedResult[] {
  const sorted = [...results];
  switch (option) {
    case "alphabetical":
      return sorted.sort((a, b) => a.word.localeCompare(b.word, 'sv'));
    case "relevance":
    default:
      return sorted.sort((a, b) => {
        // Primary: match percentage
        if (b.matchPercentage !== a.matchPercentage) {
          return b.matchPercentage - a.matchPercentage;
        }
        // Secondary: number of clues (more clues = more likely correct)
        const aClueCount = a.allClues?.length || 0;
        const bClueCount = b.allClues?.length || 0;
        if (bClueCount !== aClueCount) {
          return bClueCount - aClueCount;
        }
        // Tertiary: alphabetical
        return a.word.localeCompare(b.word, 'sv');
      });
  }
}

// Type for propagated constraints across all tabs
type PropagatedConstraints = Map<string, Map<number, Set<string>>>; // tabId → position → letters

// Max iterations for constraint propagation to prevent infinite loops
// Increased from 10 to 50 to handle large networks with 20+ linked words
const MAX_PROPAGATION_ITERATIONS = 50;

export const CombinedSearchPro = () => {
  const { isAdmin, isAuthenticated } = useAuth();
  const {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    setTabs,
    linkGroups,
    setLinkGroups,
    addTab,
    deleteTab,
    clearAllTabsExceptActive,
    isLoading: isLoadingTabs,
    isSaving,
  } = useAdvancedSearchTabs();
  
  const [propagatedConstraints, setPropagatedConstraints] = useState<PropagatedConstraints>(new Map());
  const [showOverview, setShowOverview] = useState(false);
  const cellRefs = useRef<(CrosswordCellRef | null)[]>([]);

  // Iteratively propagate constraints across all linked tabs until stable
  // This implements a full transitive propagation algorithm that handles complex networks
  const propagateConstraints = useCallback(() => {
    if (linkGroups.length === 0) {
      setPropagatedConstraints(new Map());
      return;
    }

    // Initialize with each tab's possibleLetters from their results
    // AND include filled-in letters as constraints (single-letter sets)
    let currentState: PropagatedConstraints = new Map();
    tabs.forEach(tab => {
      const tabState = new Map<number, Set<string>>();
      
      // First, add possibleLetters from search results
      if (tab.possibleLetters.size > 0) {
        for (const [pos, letters] of tab.possibleLetters) {
          tabState.set(pos, new Set(letters));
        }
      }
      
      // Then, if a cell has a filled-in letter, that's the ONLY possible letter
      // This ensures that manually typed or filled letters propagate correctly
      for (let i = 0; i < tab.letters.length; i++) {
        const letter = tab.letters[i];
        if (letter && letter.trim() !== '') {
          tabState.set(i, new Set([letter.toUpperCase()]));
        }
      }
      
      if (tabState.size > 0) {
        currentState.set(tab.id, tabState);
      }
    });

    // Helper to deep copy the state for comparison
    const cloneState = (state: PropagatedConstraints): PropagatedConstraints => {
      const clone = new Map<string, Map<number, Set<string>>>();
      for (const [tabId, posMap] of state) {
        clone.set(tabId, new Map(
          Array.from(posMap.entries()).map(([pos, letters]) => [pos, new Set(letters)])
        ));
      }
      return clone;
    };

    // Helper to compare two states
    const statesAreEqual = (a: PropagatedConstraints, b: PropagatedConstraints): boolean => {
      if (a.size !== b.size) return false;
      for (const [tabId, posMapA] of a) {
        const posMapB = b.get(tabId);
        if (!posMapB || posMapA.size !== posMapB.size) return false;
        for (const [pos, lettersA] of posMapA) {
          const lettersB = posMapB.get(pos);
          if (!lettersB || lettersA.size !== lettersB.size) return false;
          for (const letter of lettersA) {
            if (!lettersB.has(letter)) return false;
          }
        }
      }
      return true;
    };

    // Helper to filter results and recalculate possible letters
    const filterAndRecalculate = (
      results: CombinedResult[], 
      constraints: Map<number, Set<string>>
    ): Map<number, Set<string>> => {
      if (constraints.size === 0) return calculatePossibleLetters(results);
      
      // Check if any constraint is empty - then no valid results exist
      for (const [pos, allowedLetters] of constraints) {
        if (allowedLetters.size === 0) {
          // Return empty sets for all constrained positions
          const emptyResult = new Map<number, Set<string>>();
          for (const [p] of constraints) {
            emptyResult.set(p, new Set<string>());
          }
          return emptyResult;
        }
      }
      
      const filtered = results.filter(result => {
        const word = result.word.toUpperCase();
        for (const [position, allowedLetters] of constraints) {
          if (position < word.length && allowedLetters.size > 0) {
            if (!allowedLetters.has(word[position])) {
              return false;
            }
          }
        }
        return true;
      });
      
      return calculatePossibleLetters(filtered);
    };

    // Iterate until stable
    for (let iteration = 0; iteration < MAX_PROPAGATION_ITERATIONS; iteration++) {
      const nextState = cloneState(currentState);

      // Step 1: For each tab, filter results based on all current constraints and recalculate
      for (const tab of tabs) {
        if (tab.results.length === 0) continue;

        // Get ALL constraints for this tab from currentState (before this iteration's changes)
        const constraints = new Map<number, Set<string>>();
        
        // Get constraints from linked cells
        for (const group of linkGroups) {
          const myCells = group.cells.filter(c => c.tabId === tab.id);
          const otherCells = group.cells.filter(c => c.tabId !== tab.id);
          
          for (const myCell of myCells) {
            for (const otherCell of otherCells) {
              const otherPossible = currentState.get(otherCell.tabId)?.get(otherCell.cellIndex);
              // Include empty sets to propagate "impossibility" through the network
              if (otherPossible !== undefined) {
                const existing = constraints.get(myCell.cellIndex);
                if (existing) {
                  // Intersection with empty set = empty set (propagates impossibility)
                  const intersection = new Set([...existing].filter(x => otherPossible.has(x)));
                  constraints.set(myCell.cellIndex, intersection);
                } else {
                  constraints.set(myCell.cellIndex, new Set(otherPossible));
                }
              }
            }
          }
        }
        
        // Filter results and recalculate possible letters
        const newPossible = filterAndRecalculate(tab.results, constraints);
        
        // Update tab state with filtered results
        const tabState = nextState.get(tab.id);
        if (tabState) {
          for (const [pos, letters] of newPossible) {
            const existing = tabState.get(pos);
            if (existing) {
              const intersection = new Set([...existing].filter(l => letters.has(l)));
              tabState.set(pos, intersection);
            } else {
              tabState.set(pos, letters);
            }
          }
        } else {
          nextState.set(tab.id, newPossible);
        }
      }

      // Step 2: For each link group, enforce that ALL cells in the group share the SAME possible letters
      // This is the critical step that ensures linked cells are always in sync
      for (const group of linkGroups) {
        // Compute intersection of possible letters for all cells in this group
        let sharedLetters: Set<string> | null = null;
        
        for (const cell of group.cells) {
          const cellPossible = nextState.get(cell.tabId)?.get(cell.cellIndex);
          // Include empty sets - if any cell has no possibilities, the whole group should have none
          if (cellPossible !== undefined) {
            if (sharedLetters === null) {
              sharedLetters = new Set(cellPossible);
            } else {
              // Intersect: if any cell is empty, result is empty (propagates impossibility)
              sharedLetters = new Set([...sharedLetters].filter(l => cellPossible.has(l)));
            }
          }
        }
        
        // Apply the shared intersection back to ALL cells in the group
        // This ensures all linked cells have identical possible letters
        if (sharedLetters !== null) {
          for (const cell of group.cells) {
            let tabMap = nextState.get(cell.tabId);
            if (!tabMap) {
              tabMap = new Map<number, Set<string>>();
              nextState.set(cell.tabId, tabMap);
            }
            tabMap.set(cell.cellIndex, new Set(sharedLetters));
          }
        }
      }

      // Check if state has converged
      if (statesAreEqual(currentState, nextState)) {
        break;
      }
      
      currentState = nextState;
    }

    setPropagatedConstraints(currentState);
  }, [tabs, linkGroups]);

  // Trigger propagation when tabs or links change
  useEffect(() => {
    propagateConstraints();
  }, [propagateConstraints]);

  // Auto-fill letters when propagation results in exactly one possible letter for a LINKED cell
  // IMPORTANT: Auto-fill should ONLY happen for cells that are linked to OTHER tabs
  // The source tab (where user filled in letters) should never be auto-filled
  // Use a ref to track the last propagated state to avoid re-filling deleted letters
  const lastAutoFilledRef = useRef<Map<string, Map<number, string>>>(new Map());
  
  useEffect(() => {
    if (propagatedConstraints.size === 0 || linkGroups.length === 0) return;

    let hasUpdates = false;
    const tabUpdates = new Map<string, Map<number, string>>();

    // Check each tab for cells where there's exactly one possible letter
    for (const [tabId, constraints] of propagatedConstraints) {
      // Find the current tab to check if cells are empty
      const tab = tabs.find(t => t.id === tabId);
      if (!tab) continue;
      
      for (const [position, letters] of constraints) {
        // Skip if cell already has a value - user input takes priority
        const currentValue = tab.letters[position] || '';
        if (currentValue !== '') continue;
        
        // Only auto-fill if exactly one possible letter
        if (letters.size !== 1) continue;
        
        // Only auto-fill if this cell is linked to cells in OTHER tabs
        // This prevents auto-fill from affecting the source tab
        const linkGroup = linkGroups.find(g => 
          g.cells.some(c => c.tabId === tabId && c.cellIndex === position)
        );
        
        if (!linkGroup) continue;
        
        // Check that there's at least one other tab in this link group with a filled letter
        // This means we only auto-fill as a result of propagation from another tab
        const hasFilledSourceInOtherTab = linkGroup.cells.some(c => {
          if (c.tabId === tabId) return false; // Skip same tab
          const otherTab = tabs.find(t => t.id === c.tabId);
          if (!otherTab) return false;
          const otherValue = otherTab.letters[c.cellIndex] || '';
          return otherValue !== '';
        });
        
        if (!hasFilledSourceInOtherTab) continue;
        
        const [letter] = letters;
        const lastFilled = lastAutoFilledRef.current.get(tabId)?.get(position);
        
        // Only auto-fill if we haven't already filled this exact letter
        if (lastFilled !== letter) {
          if (!tabUpdates.has(tabId)) {
            tabUpdates.set(tabId, new Map());
          }
          tabUpdates.get(tabId)!.set(position, letter);
          hasUpdates = true;
        }
      }
    }

    if (hasUpdates) {
      // Update the ref to track what we're auto-filling
      for (const [tabId, updates] of tabUpdates) {
        if (!lastAutoFilledRef.current.has(tabId)) {
          lastAutoFilledRef.current.set(tabId, new Map());
        }
        for (const [pos, letter] of updates) {
          lastAutoFilledRef.current.get(tabId)!.set(pos, letter);
        }
      }
      
      setTabs(prevTabs => prevTabs.map(tab => {
        const updates = tabUpdates.get(tab.id);
        if (!updates) return tab;

        const newLetters = [...tab.letters];
        for (const [pos, letter] of updates) {
          // Double-check cell is still empty (in case of race condition)
          if (newLetters[pos] === '') {
            newLetters[pos] = letter;
          }
        }
        return { ...tab, letters: newLetters };
      }));
    }
  }, [propagatedConstraints, linkGroups, setTabs, tabs]);

  // Get link info for a specific cell
  const getCellLink = useCallback((tabId: string, cellIndex: number): CellLinkInfo | null => {
    for (const group of linkGroups) {
      if (group.cells.some(c => c.tabId === tabId && c.cellIndex === cellIndex)) {
        return { groupId: group.id, color: group.color };
      }
    }
    return null;
  }, [linkGroups]);

  // Create or extend a link between cells
  const handleCreateLink = useCallback((sourceTabId: string, sourceCellIndex: number, targetTabId: string, targetCellIndex: number) => {
    setLinkGroups(prev => {
      // Check if source is already in a group
      const sourceGroup = prev.find(g => g.cells.some(c => c.tabId === sourceTabId && c.cellIndex === sourceCellIndex));
      // Check if target is already in a group
      const targetGroup = prev.find(g => g.cells.some(c => c.tabId === targetTabId && c.cellIndex === targetCellIndex));

      if (sourceGroup && targetGroup && sourceGroup.id === targetGroup.id) {
        // Already in the same group
        return prev;
      }

      if (sourceGroup && targetGroup) {
        // Merge two groups
        const mergedCells = [...sourceGroup.cells, ...targetGroup.cells];
        return prev
          .filter(g => g.id !== sourceGroup.id && g.id !== targetGroup.id)
          .concat({ ...sourceGroup, cells: mergedCells });
      }

      if (sourceGroup) {
        // Add target to source's group
        return prev.map(g => 
          g.id === sourceGroup.id 
            ? { ...g, cells: [...g.cells, { tabId: targetTabId, cellIndex: targetCellIndex }] }
            : g
        );
      }

      if (targetGroup) {
        // Add source to target's group
        return prev.map(g => 
          g.id === targetGroup.id 
            ? { ...g, cells: [...g.cells, { tabId: sourceTabId, cellIndex: sourceCellIndex }] }
            : g
        );
      }

      // Create new group
      const usedColors = new Set(prev.map(g => g.color));
      const availableColor = LINK_COLORS.find(c => !usedColors.has(c)) || LINK_COLORS[prev.length % LINK_COLORS.length];
      
      return [...prev, {
        id: crypto.randomUUID(),
        color: availableColor,
        cells: [
          { tabId: sourceTabId, cellIndex: sourceCellIndex },
          { tabId: targetTabId, cellIndex: targetCellIndex }
        ]
      }];
    });

    // Sync letters between the linked cells
    setTabs(prevTabs => {
      const sourceTab = prevTabs.find(t => t.id === sourceTabId);
      const targetTab = prevTabs.find(t => t.id === targetTabId);
      
      if (sourceTab && targetTab) {
        const sourceLetter = sourceTab.letters[sourceCellIndex];
        const targetLetter = targetTab.letters[targetCellIndex];
        
        // If one has a letter and the other doesn't, sync them
        if (sourceLetter && !targetLetter) {
          return prevTabs.map(t => 
            t.id === targetTabId 
              ? { ...t, letters: t.letters.map((l, i) => i === targetCellIndex ? sourceLetter : l) }
              : t
          );
        } else if (targetLetter && !sourceLetter) {
          return prevTabs.map(t => 
            t.id === sourceTabId 
              ? { ...t, letters: t.letters.map((l, i) => i === sourceCellIndex ? targetLetter : l) }
              : t
          );
        }
      }
      return prevTabs;
    });
  }, []);

  // Remove a cell from its link group
  const handleRemoveLink = useCallback((tabId: string, cellIndex: number) => {
    setLinkGroups(prev => {
      return prev
        .map(group => ({
          ...group,
          cells: group.cells.filter(c => !(c.tabId === tabId && c.cellIndex === cellIndex))
        }))
        .filter(group => group.cells.length >= 2); // Remove groups with less than 2 cells
    });
  }, []);

  // activeTab is now provided by the hook

  const updateActiveTab = (updates: Partial<SearchTab>) => {
    setTabs(prev => prev.map(tab => 
      tab.id === activeTabId ? { ...tab, ...updates } : tab
    ));
  };

  // Get linked constraints for a specific tab from OTHER tabs only
  // Never includes the tab's own possibleLetters to avoid stale constraints
  // when letters are removed after a search
  const getLinkedConstraints = useCallback((tabId: string): Map<number, Set<string>> => {
    const constraints = new Map<number, Set<string>>();
    
    for (const group of linkGroups) {
      const myCells = group.cells.filter(c => c.tabId === tabId);
      const otherCells = group.cells.filter(c => c.tabId !== tabId);
      
      for (const myCell of myCells) {
        for (const otherCell of otherCells) {
          const otherPropagated = propagatedConstraints.get(otherCell.tabId);
          if (otherPropagated && otherPropagated.size > 0) {
            const allowedLetters = otherPropagated.get(otherCell.cellIndex);
            if (allowedLetters && allowedLetters.size > 0) {
              const existing = constraints.get(myCell.cellIndex);
              if (existing) {
                const intersection = new Set([...existing].filter(x => allowedLetters.has(x)));
                constraints.set(myCell.cellIndex, intersection);
              } else {
                constraints.set(myCell.cellIndex, new Set(allowedLetters));
              }
            }
          }
        }
      }
    }
    
    return constraints;
  }, [linkGroups, propagatedConstraints]);

  // Filter results based on linked constraints from other tabs
  const filterByLinkedConstraints = useCallback((
    results: CombinedResult[], 
    constraints: Map<number, Set<string>>
  ): CombinedResult[] => {
    if (constraints.size === 0) return results;
    
    return results.filter(result => {
      const word = result.word.toUpperCase();
      
      for (const [position, allowedLetters] of constraints) {
        if (position < word.length && allowedLetters.size > 0) {
          if (!allowedLetters.has(word[position])) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, []);

  // Get constraint info for a specific cell (for UI display)
  const getCellConstraint = useCallback((cellIndex: number): Set<string> | null => {
    // If cell already has a letter, no constraint to show
    if (activeTab.letters[cellIndex]) return null;
    
    const constraints = getLinkedConstraints(activeTabId);
    return constraints.get(cellIndex) || null;
  }, [activeTabId, activeTab.letters, getLinkedConstraints]);


  const handleLetterChange = useCallback((index: number, value: string) => {
    setTabs(prev => {
      const currentTab = prev.find(t => t.id === activeTabId);
      if (!currentTab) return prev;

      // If period is entered, set word length to the position before the period
      if (value === '.') {
        return prev.map(tab => {
          if (tab.id !== activeTabId) return tab;
          const newLetters = [...tab.letters];
          newLetters[index] = '';
          return { ...tab, letters: newLetters, selectedLength: index > 0 ? index : null };
        });
      }

      // Check if this cell is part of a link group
      const linkGroup = linkGroups.find(g => 
        g.cells.some(c => c.tabId === activeTabId && c.cellIndex === index)
      );

      if (linkGroup) {
        // Update all linked cells
        return prev.map(tab => {
          const linkedCell = linkGroup.cells.find(c => c.tabId === tab.id);
          if (linkedCell) {
            const newLetters = [...tab.letters];
            newLetters[linkedCell.cellIndex] = value;
            return { ...tab, letters: newLetters };
          }
          return tab;
        });
      } else {
        // Normal update for non-linked cell
        return prev.map(tab => {
          if (tab.id !== activeTabId) return tab;
          const newLetters = [...tab.letters];
          newLetters[index] = value;
          return { ...tab, letters: newLetters };
        });
      }
    });
  }, [activeTabId, linkGroups]);

  const handleAutoAdvance = useCallback((index: number) => {
    if (index < MAX_LENGTH - 1) {
      cellRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!activeTab.letters[index] && index > 0) {
        e.preventDefault();
        setTabs(prev => prev.map(tab => {
          if (tab.id !== activeTabId) return tab;
          const newLetters = [...tab.letters];
          newLetters[index - 1] = "";
          return { ...tab, letters: newLetters };
        }));
        cellRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      cellRefs.current[index - 1]?.focus();
    } else if ((e.key === "ArrowRight" || e.key === " ") && index < MAX_LENGTH - 1) {
      e.preventDefault();
      cellRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    } else if (e.key === "Home") {
      e.preventDefault();
      cellRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      cellRefs.current[MAX_LENGTH - 1]?.focus();
    }
  }, [activeTab.letters, activeTabId]);

  const handleReset = () => {
    updateActiveTab({ letters: Array(MAX_LENGTH).fill("") });
    setTimeout(() => cellRefs.current[0]?.focus(), 50);
  };

  // buildLetterPattern removed - now inlined in handleSearchForTab (1.1)

  // Build constraint-aware search pattern that respects linked constraints
  const buildConstraintAwarePattern = useCallback((
    basePattern: string, 
    linkedConstraints: Map<number, Set<string>>,
    letters: string[]
  ): { pattern: string; constraintPositions: Map<number, string[]> } => {
    // For SQL LIKE, we can't do [ABC] patterns, so we need to track positions
    // that need post-query filtering with specific allowed letters
    const constraintPositions = new Map<number, string[]>();
    
    for (const [position, allowedLetters] of linkedConstraints) {
      // Only apply constraint if the position is within our pattern and doesn't already have a letter
      if (!letters[position] && allowedLetters.size > 0 && allowedLetters.size <= 29) {
        constraintPositions.set(position, [...allowedLetters]);
      }
    }
    
    return { pattern: basePattern, constraintPositions };
  }, []);

  // buildConstraintFilters removed (1.4) - was unused dead code

   // State for batch search progress (3.3)
  const [searchAllProgress, setSearchAllProgress] = useState<{
    current: number;
    total: number;
    round: number;
    totalRounds: number;
  } | null>(null);

  // 1.1: Refactored to accept a target tab ID, so "search all tabs" works correctly
  const handleSearchForTab = async (targetTabId: string) => {
    const targetTab = tabs.find(t => t.id === targetTabId);
    if (!targetTab) return;

    // Build letter pattern for the target tab
    const buildPatternForTab = (tab: SearchTab): string => {
      const { selectedLength, letters } = tab;
      if (selectedLength === null) {
        let lastFilledIndex = -1;
        for (let i = MAX_LENGTH - 1; i >= 0; i--) {
          if (letters[i]) {
            lastFilledIndex = i;
            break;
          }
        }
        if (lastFilledIndex === -1) return "";
        return letters.slice(0, lastFilledIndex + 1).map((l) => l || "?").join("") + "*";
      } else {
        return letters.slice(0, selectedLength).map((l) => l || "?").join("");
      }
    };

    const letterPattern = buildPatternForTab(targetTab);
    const { clueQuery, selectedLength, sortOption, languageFilter, letters } = targetTab;
    
    // Get linked constraints for this tab
    const linkedConstraints = getLinkedConstraints(targetTabId);
    
    // Minimikrav för sökning - kräver ifylld bokstav ELLER minst en länkad cell till annan flik
    const hasFilledLetter = letters.some(l => l.trim() !== "");
    const hasLinkedCells = linkGroups.some(g => 
      g.cells.some(c => c.tabId === targetTabId) && 
      g.cells.some(c => c.tabId !== targetTabId)
    );

    if (!hasFilledLetter && !hasLinkedCells) {
      setTabs(prev => prev.map(tab => 
        tab.id === targetTabId ? { ...tab, error: "Ange minst en bokstav eller koppla en cell till en annan flik" } : tab
      ));
      return;
    }

    setTabs(prev => prev.map(tab => 
      tab.id === targetTabId ? { ...tab, isLoading: true, error: null, results: [], hasSearched: true, currentPage: 1 } : tab
    ));

    try {
      // Build constraint-aware pattern info
      const { constraintPositions } = buildConstraintAwarePattern(letterPattern, linkedConstraints, letters);
      
      // Convert constraint positions Map to object for JSON
      const constraintPositionsObj: Record<number, string[]> = {};
      for (const [pos, ltrs] of constraintPositions) {
        constraintPositionsObj[pos] = ltrs;
      }

      // Call edge function for complete database search
      const { data: responseData, error: funcError } = await supabase.functions.invoke('advanced-search', {
        body: {
          letterPattern,
          clueQuery: clueQuery.trim(),
          selectedLength,
          languageFilter,
          constraintPositions: constraintPositionsObj
        }
      });

      if (funcError) throw funcError;
      
      if (responseData?.error) {
        throw new Error(responseData.error);
      }

      const serverResults = responseData?.results || [];
      
      // Map server results to CombinedResult format
      const combinedResults: CombinedResult[] = serverResults.map((r: any) => ({
        word: r.word,
        wordLength: r.wordLength,
        clues: r.clues || [],
        allClues: r.allClues || [],
        matchPercentage: r.matchPercentage,
        language: r.language
      }));

      const sortedResults = sortResults(combinedResults, sortOption);

      // Calculate possible letters for each position from results
      const possibleLetters = calculatePossibleLetters(sortedResults);

      // Calculate the filtered count for this tab (after search completes)
      const searchLinkedConstraints = getLinkedConstraints(targetTabId);
      const languageFiltered = filterByLanguage(sortedResults, languageFilter);
      const filteredCount = filterByLinkedConstraints(languageFiltered, searchLinkedConstraints).length;

      setTabs(prev => prev.map(tab => 
        tab.id === targetTabId ? { 
          ...tab,
          results: sortedResults, 
          isLoading: false,
          possibleLetters: possibleLetters,
          lastSeenFilteredCount: filteredCount,
        } : tab
      ));

      // Log search for statistics - skip if admin
      if (!isAdmin) {
        supabase.from('search_logs').insert({
          search_term: `${clueQuery} | ${letterPattern}`,
          search_type: 'combined',
          results_count: sortedResults.length,
        }).then(() => {});
      }

    } catch (err) {
      let errorMessage = 'Ett fel uppstod';
      
      if (err instanceof Error) {
        if (err.message.includes('timeout') || err.message.includes('statement canceled') || err.message.includes('canceling statement')) {
          errorMessage = 'Sökningen tog för lång tid. Försök att ange fler bokstäver, ordlängd eller en mer specifik ledtråd.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setTabs(prev => prev.map(tab => 
        tab.id === targetTabId ? { ...tab, error: errorMessage, isLoading: false } : tab
      ));
      console.error('Combined search error:', err);
    }
  };

  // Wrapper for active tab search (backward compatible)
  const handleSearch = () => handleSearchForTab(activeTabId);

  const handleSortChange = (option: SortOption) => {
    updateActiveTab({ sortOption: option });
    if (activeTab.results.length > 0) {
      const sorted = sortResults(activeTab.results, option);
      updateActiveTab({ results: sorted, sortOption: option });
    }
  };

  const addNewTab = async () => {
    const newTab = await addTab();
    setActiveTabId(newTab.id);
  };

  const closeTab = async (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteTab(tabId);
  };

  const lengths = Array.from({ length: MAX_LENGTH }, (_, i) => i + 1);

  // Auto-search when letters or length change (debounced)
  const linkedConstraintsForAutoSearch = getLinkedConstraints(activeTabId);
  useAutoSearch({
    letters: activeTab.letters,
    selectedLength: activeTab.selectedLength,
    hasSearched: activeTab.hasSearched,
    isLoading: activeTab.isLoading,
    onSearch: handleSearch,
    debounceMs: 600, // Slightly longer debounce for typing
    enabled: true,
    hasLinkedConstraints: linkedConstraintsForAutoSearch.size > 0,
  });

  // Navigate to next/prev tab
  const handleNextTab = useCallback(() => {
    const sortedTabs = [...tabs].sort((a, b) => {
      const aName = getTabDisplayName(a.clueQuery);
      const bName = getTabDisplayName(b.clueQuery);
      const aIsNew = aName === "Ny";
      const bIsNew = bName === "Ny";
      if (aIsNew && !bIsNew) return 1;
      if (!aIsNew && bIsNew) return -1;
      return aName.localeCompare(bName, 'sv');
    });
    const currentIndex = sortedTabs.findIndex(t => t.id === activeTabId);
    const nextIndex = (currentIndex + 1) % sortedTabs.length;
    setActiveTabId(sortedTabs[nextIndex].id);
  }, [tabs, activeTabId, setActiveTabId]);

  const handlePrevTab = useCallback(() => {
    const sortedTabs = [...tabs].sort((a, b) => {
      const aName = getTabDisplayName(a.clueQuery);
      const bName = getTabDisplayName(b.clueQuery);
      const aIsNew = aName === "Ny";
      const bIsNew = bName === "Ny";
      if (aIsNew && !bIsNew) return 1;
      if (!aIsNew && bIsNew) return -1;
      return aName.localeCompare(bName, 'sv');
    });
    const currentIndex = sortedTabs.findIndex(t => t.id === activeTabId);
    const prevIndex = (currentIndex - 1 + sortedTabs.length) % sortedTabs.length;
    setActiveTabId(sortedTabs[prevIndex].id);
  }, [tabs, activeTabId, setActiveTabId]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: handleSearch,
    onNewTab: addNewTab,
    onNextTab: handleNextTab,
    onPrevTab: handlePrevTab,
  });

  // Calculate filtered result count for any tab (for tab display)
  const getFilteredResultCountForTab = useCallback((tab: SearchTab): number => {
    if (!tab.hasSearched || tab.results.length === 0) return 0;
    
    const linkedConstraints = getLinkedConstraints(tab.id);
    const languageFiltered = filterByLanguage(tab.results, tab.languageFilter);
    const fullyFiltered = filterByLinkedConstraints(languageFiltered, linkedConstraints);
    
    return fullyFiltered.length;
  }, [getLinkedConstraints, filterByLinkedConstraints]);

  // Handle tab click - update lastSeenFilteredCount when switching tabs
  const handleTabClick = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      const currentCount = getFilteredResultCountForTab(tab);
      setTabs(prev => prev.map(t => 
        t.id === tabId ? { ...t, lastSeenFilteredCount: currentCount } : t
      ));
    }
    setActiveTabId(tabId);
  }, [tabs, getFilteredResultCountForTab, setTabs, setActiveTabId]);

  // Show loading state while tabs are being loaded
  if (isLoadingTabs) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Laddar sökningar...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab bar - wraps to multiple lines, sorted alphabetically (empty clues go last) */}
      <div className="flex flex-wrap items-center gap-1 bg-muted/30 rounded-lg p-1.5">
        {[...tabs].sort((a, b) => {
          const aName = getTabDisplayName(a.clueQuery);
          const bName = getTabDisplayName(b.clueQuery);
          const aIsNew = aName === "Ny";
          const bIsNew = bName === "Ny";
          // New tabs (empty clue) go last
          if (aIsNew && !bIsNew) return 1;
          if (!aIsNew && bIsNew) return -1;
          // Both have clues or both are new: sort alphabetically
          return aName.localeCompare(bName, 'sv');
        }).map((tab) => {
          const filteredCount = getFilteredResultCountForTab(tab);
          const hasDecreased = tab.lastSeenFilteredCount !== undefined && 
                               filteredCount < tab.lastSeenFilteredCount &&
                               tab.id !== activeTabId;
          
          return (
            <div
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                "group flex items-center gap-1 px-2 py-1.5 rounded text-xs cursor-pointer transition-all",
                tab.id === activeTabId
                  ? "bg-background shadow-sm border border-border text-foreground"
                  : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
              )}
            >
              <span className={cn(
                "max-w-[180px] truncate",
                hasDecreased ? "font-bold" : "font-medium"
              )}>
                {getTabDisplayName(tab.clueQuery)}
                {tab.hasSearched && filteredCount > 0 && (
                  <span className="ml-1 text-muted-foreground font-normal">
                    ({filteredCount})
                  </span>
                )}
              </span>
              <button
                onClick={(e) => closeTab(tab.id, e)}
                className="p-0.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                title="Stäng"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <button
          onClick={addNewTab}
          className="flex items-center justify-center p-2 rounded-md text-muted-foreground hover:bg-background/50 hover:text-foreground transition-colors"
          title="Ny sökning"
        >
          <Plus className="w-4 h-4" />
        </button>
        
        {/* Save indicator and clear button - right corner */}
        <div className="ml-auto flex items-center gap-1">
          {isAuthenticated && (
            <div className="flex items-center text-xs text-muted-foreground" title="Sparas automatiskt">
              {isSaving ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
            </div>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowOverview(prev => !prev)}
                  className={cn(
                    "p-1 rounded-md transition-colors",
                    showOverview
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <LayoutGrid className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Översikt
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="p-1 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30"
                title="Rensa alla flikar utom aktiva"
                disabled={tabs.length <= 1}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rensa flikar</AlertDialogTitle>
                <AlertDialogDescription>
                  Vill du rensa alla sökflikar utom den aktiva? Detta kan inte ångras.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction onClick={clearAllTabsExceptActive}>
                  Rensa
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Overview panel */}
      {showOverview && (
        <AdvancedSearchOverview
          tabs={tabs}
          activeTabId={activeTabId}
          linkGroups={linkGroups}
          onTabClick={handleTabClick}
          getFilteredResultCount={getFilteredResultCountForTab}
        />
      )}

      <div className="space-y-4">
        {/* Clue input + Language filter in one row */}
        <div className="flex items-end gap-4">
          <div className="space-y-2 flex-1">
            <label className="text-sm font-medium text-foreground">Ledtråd / Fråga</label>
            <Input
              value={activeTab.clueQuery}
              onChange={(e) => updateActiveTab({ clueQuery: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="T.ex. huvudstad i sverige..."
              className="w-full"
            />
          </div>

          {/* Language filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Språk</label>
            <div className="flex items-center gap-2 h-9">
              {LANGUAGE_FILTER_OPTIONS.map((lang) => {
                const isActive = activeTab.languageFilter === 0 || (activeTab.languageFilter & lang.value) !== 0;
                return (
                  <button
                    key={lang.value}
                    onClick={() => {
                      const currentFilter = activeTab.languageFilter;
                      let newFilter: number;
                      
                      if (currentFilter === 0) {
                        newFilter = lang.value;
                      } else if ((currentFilter & lang.value) !== 0) {
                        newFilter = currentFilter & ~lang.value;
                        if (newFilter === 0) newFilter = 0;
                      } else {
                        newFilter = currentFilter | lang.value;
                      }
                      
                      updateActiveTab({ languageFilter: newFilter, currentPage: 1 });
                    }}
                    className={cn(
                      "text-xl transition-all duration-150 hover:scale-110",
                      isActive ? "opacity-100" : "opacity-30 grayscale"
                    )}
                    title={lang.name}
                  >
                    {lang.flag}
                  </button>
                );
              })}
              {activeTab.languageFilter !== 0 && (
                <button
                  onClick={() => updateActiveTab({ languageFilter: 0, currentPage: 1 })}
                  className="text-xs text-muted-foreground hover:text-foreground underline ml-1"
                >
                  Alla
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Bokstavsmönster</label>
          
          <div className="overflow-x-auto -mx-2 px-2">
            <div className="inline-flex items-start gap-0">
              <div className="flex flex-col items-center">
                <div className="h-5" />
                <div className="h-5" />
                <button
                  onClick={handleReset}
                  className="w-[22px] h-7 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded transition-colors"
                  title="Rensa"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="h-3" /> {/* Space for link dropdown row */}
              </div>

              <div className="flex flex-col items-center w-[22px]">
                <div className="h-5 flex items-center justify-center text-xs text-muted-foreground font-medium">
                  ?
                </div>
                <div className="h-5 flex items-center justify-center">
                  <input
                    type="radio"
                    name={`combinedWordLength-${activeTabId}`}
                    checked={activeTab.selectedLength === null}
                    onChange={() => updateActiveTab({ selectedLength: null })}
                    className="w-3 h-3 cursor-pointer accent-primary"
                  />
                </div>
                <div className="h-7" />
                <div className="h-3" /> {/* Space for link dropdown row */}
              </div>

              {lengths.map((length, index) => {
                const cellLink = getCellLink(activeTabId, index);
                const cellConstraint = getCellConstraint(index);
                const hasConstraint = cellConstraint && cellConstraint.size > 0 && !activeTab.letters[index];
                
                return (
                  <div key={length} className="flex flex-col items-center w-[22px] group">
                    <div className="h-5 flex items-center justify-center text-xs text-muted-foreground">
                      {length}
                    </div>
                    <div className="h-5 flex items-center justify-center">
                      <input
                        type="radio"
                        name={`combinedWordLength-${activeTabId}`}
                        checked={activeTab.selectedLength === length}
                        onChange={() => updateActiveTab({ selectedLength: length })}
                        className="w-3 h-3 cursor-pointer accent-primary"
                      />
                    </div>
                    <div className="relative">
                      <CrosswordCell
                        ref={(el) => { cellRefs.current[index] = el; }}
                        value={activeTab.letters[index]}
                        onChange={(value) => handleLetterChange(index, value)}
                        onAutoAdvance={() => handleAutoAdvance(index)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        tabIndex={index}
                        dimmed={activeTab.selectedLength !== null && index >= activeTab.selectedLength}
                        linkColor={cellLink?.color || null}
                        hasConstraint={hasConstraint && !activeTab.letters[index]}
                      />
                    </div>
                    <CellLinkDropdown
                      tabId={activeTabId}
                      cellIndex={index}
                      currentLink={cellLink}
                      allTabs={tabs.map(t => ({ id: t.id, name: getTabDisplayName(t.clueQuery), letters: t.letters, selectedLength: t.selectedLength }))}
                      linkGroups={linkGroups}
                      onCreateLink={handleCreateLink}
                      onRemoveLink={handleRemoveLink}
                    />
                    {hasConstraint && !activeTab.letters[index] && (
                      <div 
                        className="text-[8px] font-medium text-primary bg-primary/20 px-1 rounded text-center"
                        title={`Möjliga: ${Array.from(cellConstraint).sort().join(', ')}`}
                      >
                        {cellConstraint.size}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleSearch} 
            disabled={activeTab.isLoading}
            className="flex-1"
          >
            {activeTab.isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Söker...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Sök
              </>
            )}
          </Button>
          
          {tabs.length > 1 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={async () => {
                      // Iterative batch search: multiple rounds until constraints stabilize
                      const searchableTabs = tabs.filter(t => t.selectedLength);
                      if (searchableTabs.length === 0) return;
                      
                      const MAX_ROUNDS = 3;
                      
                      for (let round = 0; round < MAX_ROUNDS; round++) {
                        const tabsToSearch = round === 0 
                          ? searchableTabs 
                          : searchableTabs.filter(t => {
                              // Only re-search tabs whose linked constraints changed
                              const constraints = getLinkedConstraints(t.id);
                              if (constraints.size === 0) return false;
                              // Check if any constraint position has fewer letters than 29
                              for (const [, letters] of constraints) {
                                if (letters.size > 0 && letters.size < 29) return true;
                              }
                              return false;
                            });
                        
                        if (tabsToSearch.length === 0) break;
                        
                        for (let i = 0; i < tabsToSearch.length; i++) {
                          setSearchAllProgress({ 
                            current: i + 1, 
                            total: tabsToSearch.length,
                            round: round + 1,
                            totalRounds: MAX_ROUNDS,
                          });
                          await handleSearchForTab(tabsToSearch[i].id);
                        }
                        
                        // After round 1+, if no tabs needed re-searching, we're done
                        if (round > 0 && tabsToSearch.length === searchableTabs.length) break;
                      }
                      
                      setSearchAllProgress(null);
                    }}
                    disabled={activeTab.isLoading || searchAllProgress !== null}
                    title="Sök alla flikar (iterativt)"
                  >
                    <RefreshCw className={cn("w-4 h-4", searchAllProgress !== null && "animate-spin")} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Sök alla flikar (uppdaterar alla tabbar)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* 3.3: Progress indicator for batch search */}
        {searchAllProgress && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Omgång {searchAllProgress.round}/{searchAllProgress.totalRounds}: Söker flik {searchAllProgress.current} av {searchAllProgress.total}...</span>
              <span>{Math.round((searchAllProgress.current / searchAllProgress.total) * 100)}%</span>
            </div>
            <Progress value={(searchAllProgress.current / searchAllProgress.total) * 100} className="h-2" />
          </div>
        )}
      </div>

      {activeTab.error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {activeTab.error}
        </div>
      )}

      {(() => {
        // Apply both language filter AND linked constraints
        const linkedConstraints = getLinkedConstraints(activeTabId);
        const languageFiltered = filterByLanguage(activeTab.results, activeTab.languageFilter);
        const fullyFilteredResults = filterByLinkedConstraints(languageFiltered, linkedConstraints);
        const hasLinkedConstraints = linkedConstraints.size > 0;
        
        if (activeTab.hasSearched && !activeTab.isLoading && fullyFilteredResults.length === 0 && !activeTab.error) {
          // 3.1: Show which tabs are causing link conflicts
          let conflictMessage = "Inga resultat hittades";
          if (activeTab.results.length > 0) {
            if (hasLinkedConstraints) {
              const conflictingTabNames = linkGroups
                .filter(g => g.cells.some(c => c.tabId === activeTabId))
                .flatMap(g => g.cells.filter(c => c.tabId !== activeTabId))
                .map(c => tabs.find(t => t.id === c.tabId))
                .filter(Boolean)
                .map(t => getTabDisplayName(t!.clueQuery))
                .filter((v, i, a) => a.indexOf(v) === i);
              
              conflictMessage = conflictingTabNames.length > 0
                ? `Inga resultat matchar begränsningarna från: ${conflictingTabNames.join(', ')}`
                : "Inga resultat matchar begränsningarna från länkade sökningar";
            } else if (activeTab.languageFilter !== 0) {
              conflictMessage = "Inga resultat matchar det valda språkfiltret";
            }
          }
          
          return (
            <div className="text-center py-8 text-muted-foreground">
              {conflictMessage}
            </div>
          );
        }
        
        if (fullyFilteredResults.length === 0) return null;
        
        const totalPages = Math.ceil(fullyFilteredResults.length / ITEMS_PER_PAGE);
        const startIndex = (activeTab.currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, fullyFilteredResults.length);
        const paginatedResults = fullyFilteredResults.slice(startIndex, endIndex);

        const getPageNumbers = () => {
          const pages: (number | "ellipsis")[] = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (activeTab.currentPage > 3) pages.push("ellipsis");
            for (let i = Math.max(2, activeTab.currentPage - 1); i <= Math.min(totalPages - 1, activeTab.currentPage + 1); i++) {
              pages.push(i);
            }
            if (activeTab.currentPage < totalPages - 2) pages.push("ellipsis");
            pages.push(totalPages);
          }
          return pages;
        };

        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {fullyFilteredResults.length} resultat
                {(activeTab.languageFilter !== 0 || hasLinkedConstraints) && activeTab.results.length !== fullyFilteredResults.length && (
                  <span className="text-xs ml-1">({activeTab.results.length} totalt)</span>
                )}
                {totalPages > 1 && ` (visar ${startIndex + 1}-${endIndex})`}
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <Select value={activeTab.sortOption} onValueChange={(v) => handleSortChange(v as SortOption)}>
                  <SelectTrigger className="w-[160px] h-8 text-sm">
                    <SelectValue placeholder="Sortering" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevans</SelectItem>
                    <SelectItem value="alphabetical">Bokstavsordning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              {paginatedResults.map((result, index) => (
                <div 
                  key={index}
                  className={cn(
                    "p-3 rounded-lg border border-border bg-secondary/30",
                    "hover:bg-secondary/50 transition-colors"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{result.word}</span>
                      <button
                        onClick={() => {
                          // Fill letters from clicked result into the form
                          const word = result.word.toUpperCase();
                          const newLetters = Array(MAX_LENGTH).fill("");
                          for (let i = 0; i < word.length && i < MAX_LENGTH; i++) {
                            newLetters[i] = word[i];
                          }
                          
                          // Beräkna possibleLetters för endast det valda ordet
                          const selectedWordPossible = new Map<number, Set<string>>();
                          for (let i = 0; i < word.length; i++) {
                            selectedWordPossible.set(i, new Set([word[i]]));
                          }
                          
                          // Clear lastAutoFilledRef for OTHER tabs so propagation can happen again
                          // This allows linked cells in other tabs to receive the new letter
                          for (const [tabId] of lastAutoFilledRef.current) {
                            if (tabId !== activeTabId) {
                              lastAutoFilledRef.current.delete(tabId);
                            }
                          }
                          
                          updateActiveTab({ 
                            letters: newLetters, 
                            selectedLength: result.wordLength,
                            possibleLetters: selectedWordPossible,
                          });
                        }}
                        className="p-1 rounded hover:bg-primary/20 transition-colors"
                        title="Fyll i bokstäver"
                      >
                        <PenLine className="w-4 h-4 text-primary" />
                      </button>
                      <button
                        onClick={() => {
                          window.open(`https://www.google.com/search?q=${encodeURIComponent(result.word)}`, '_blank');
                        }}
                        className="p-1 rounded hover:bg-primary/20 transition-colors"
                        title="Sök på Google"
                      >
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      </button>
                      {isAdmin ? (
                        <LanguageFlagsEditor 
                          word={result.word} 
                          language={result.language}
                          onLanguageChange={(newLang) => {
                            setTabs(prev => prev.map(tab => {
                              if (tab.id !== activeTabId) return tab;
                              return {
                                ...tab,
                                results: tab.results.map(r => 
                                  r.word === result.word ? { ...r, language: newLang } : r
                                )
                              };
                            }));
                          }}
                        />
                      ) : (
                        <LanguageFlags language={result.language} />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                        {result.matchPercentage}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {result.wordLength} bokstäver
                      </span>
                      {isAdmin && (
                        <AdminDeleteWordButton 
                          word={result.word}
                          onDeleted={() => {
                            setTabs(prev => prev.map(tab => {
                              if (tab.id !== activeTabId) return tab;
                              return {
                                ...tab,
                                results: tab.results.filter(r => r.word !== result.word)
                              };
                            }));
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {(() => {
                      // Filter clues by selected language filter
                      const filteredClues = activeTab.languageFilter === 0 
                        ? result.allClues 
                        : result.allClues.filter(c => (c.language & activeTab.languageFilter) !== 0);
                      
                      if (filteredClues.length === 0) {
                        return (
                          <span className="text-xs italic text-muted-foreground/70">
                            Inga ledtrådar hittades
                          </span>
                        );
                      }
                      
                      // Group clues by language
                      const cluesByLang = new Map<number, ClueWithLanguage[]>();
                      filteredClues.forEach(clueObj => {
                        const lang = clueObj.language;
                        const existing = cluesByLang.get(lang) || [];
                        existing.push(clueObj);
                        cluesByLang.set(lang, existing);
                      });
                      
                      // Sort languages: Swedish (1) first, then others
                      const sortedLangs = Array.from(cluesByLang.keys()).sort((a, b) => {
                        if (a === LANGUAGE_FLAGS.SWEDISH) return -1;
                        if (b === LANGUAGE_FLAGS.SWEDISH) return 1;
                        return a - b;
                      });
                      
                      // Get flag for language (skip Swedish)
                      const getFlag = (lang: number): string | null => {
                        const langOption = LANGUAGE_FILTER_OPTIONS.find(l => l.value === lang);
                        return langOption?.flag || null;
                      };
                      
                      return (
                        <div className="space-y-1">
                          {sortedLangs.map((lang, langIdx) => {
                            const langClues = cluesByLang.get(lang) || [];
                            const displayClues = langClues.slice(0, 10);
                            const flag = lang !== LANGUAGE_FLAGS.SWEDISH ? getFlag(lang) : null;
                            
                            return (
                              <div key={lang} className={cn("flex flex-wrap items-center gap-x-1 gap-y-0.5", langIdx > 0 && "pt-1")}>
                                {flag && <span className="mr-1">{flag}</span>}
                                {displayClues.map((clueObj, i, arr) => {
                                  const isMatchingClue = result.clues.includes(clueObj.clue);
                                  return (
                                    <span key={i} className="inline-flex items-center">
                                      {isMatchingClue ? highlightMatches(clueObj.clue, activeTab.clueQuery) : clueObj.clue}
                                      {i < arr.length - 1 && <span className="mx-1">•</span>}
                                    </span>
                                  );
                                })}
                                {langClues.length > 10 && (
                                  <span className="text-xs ml-1">(+{langClues.length - 10})</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => updateActiveTab({ currentPage: Math.max(1, activeTab.currentPage - 1) })}
                      className={activeTab.currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  {getPageNumbers().map((page, index) =>
                    page === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => updateActiveTab({ currentPage: page })}
                          isActive={activeTab.currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => updateActiveTab({ currentPage: Math.min(totalPages, activeTab.currentPage + 1) })}
                      className={activeTab.currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        );
      })()}
    </div>
  );
};
